import React, { useEffect, useImperativeHandle, useRef, useState } from 'react';
import {
    Box,
    Typography,
    Checkbox,
    Radio,
    RadioGroup,
    FormControlLabel,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Slider,
    Stack,
    TextField,
    InputAdornment,
    IconButton,
} from '@mui/material';
import { Add, Remove } from '@mui/icons-material';
import CustomButton from '../Button/CustomButton';
import { theme } from '../../constants/theme';
import { useThemeContext } from '../../hook/useThemeManager';
import { FONT_ENUM } from '../../types/global/fonts.types';
import { IFilter } from '../../redux/appConfigurator/types';
import { Libraries, useJsApiLoader } from '@react-google-maps/api';
import PlacesAutocomplete, { geocodeByAddress, getLatLng } from 'react-places-autocomplete';
import ClearIcon from '@mui/icons-material/Clear'; // Importing Clear Icon for "X" button
import { DateRange } from 'react-date-range';
import 'react-date-range/dist/styles.css'; // Main style file
import 'react-date-range/dist/theme/default.css'; // Theme CSS file
import locale from 'date-fns/locale/en-US';
import { isEmpty } from '../../utils/lodash';
import moment from 'moment';
import { formatDate } from '../../utils/AppUtils';
import CustomizedAutocomplete from '../CustomizedMultipleInput/CustomizedAutocomplete';
const handleLocalityFetch = (data: any[]) => {
    return data
        .filter(item => item.types.includes('locality')) // Filter by "locality" type
        .reduce((shortest, current) => {
            return current.short_name.length < shortest.length ? current.short_name : shortest;
        }, data[0].short_name);
};

interface ActiveSections {
    [key: string]: boolean;
}

export interface IFilterRef {
    onUpdateFilter(updatedForm: any): void;
}

export interface IFilterProps {
    filter?: IFilter;
    onApply?: (payload: any) => void;
    onClearFilter?: () => void;
    onClose?: VoidFunction;
    onFilterAdjusted?: (active: boolean) => void;
}

const Filter = React.forwardRef<IFilterRef, IFilterProps>((props: IFilterProps, ref) => {
    const { filter, onApply, onClearFilter, onClose, onFilterAdjusted } = props;
    const [activeSections, setActiveSections] = useState<ActiveSections>({});
    const [formData, setFormData] = useState<{ [key: string]: any }>({});
    const [sliderValue, setSliderValue] = useState<[number, number]>([0, 100]);
    const libraries = useRef<Libraries>(['places']);

    const [state, setState] = useState<any>([
        {
            startDate: new Date(),
            endDate: new Date(),
            key: 'selection',
            color: '#ff6347', // Custom color for the selected range
        },
    ]);
    const { themeStyle } = useThemeContext();
    const { isLoaded } = useJsApiLoader({
        id: 'google-map-script',
        googleMapsApiKey: process.env.REACT_APP_GOOGLE_API_KEY!,
        libraries: libraries.current,
        version: '3',
        language: 'EN',
    });

    const toggleSection = (field: string): void => {
        setActiveSections(prevState => ({
            ...prevState,
            [field]: !prevState[field],
        }));
    };

    const handleSliderChange = (event: Event, newValue: number[], key: string[]): void => {
        setSliderValue(newValue as [number, number]);
        if (key[0]) {
            handleChange(key[0], newValue[0] as unknown as [number, number], `${newValue[0]} - ${newValue[1]}`);
        }

        if (key[1]) {
            handleChange(key[1], newValue[1] as unknown as [number, number], `${newValue[0]} - ${newValue[1]}`);
        }
        // handleFilterDisplayValues(key[0], `${newValue[0]} - ${newValue[1]}`);
    };

    const handleChange = (storeKey: string, value: any, displayValue?: any) => {
        setFormData(prevData => ({
            ...prevData,
            [storeKey]: {
                value,
                displayValue,
            },
        }));
    };

    useEffect(() => {
        onFilterAdjusted && onFilterAdjusted(!isEmpty(formData));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formData]);

    const handleLocationSelect = (storeKey: string, address: string, displayValue?: string) => {
        let resultSelections: any[];
        geocodeByAddress(address)
            .then(results => {
                resultSelections = results;
                // const shortName = handleLocalityFetch(results[0].address_components);
                // handleFilterDisplayValues(shortName, storeKey);
                return getLatLng(results[0]);
            })
            .then(latLng => {
                const shortName = handleLocalityFetch(resultSelections[0].address_components);
                handleChange(storeKey, { address, coordinates: latLng }, shortName);
            })
            .catch(error => console.error('Error', error));
    };

    const clearLocationInput = (storeKey: string) => {
        setFormData(prevData => ({
            ...prevData,
            [storeKey]: '',
        }));
    };

    const handleOnApplyClick = () => {
        onApply && onApply(formData);
        onClose && onClose();
    };

    const handleOnClearFilter = () => {
        onClearFilter && onClearFilter();
        setActiveSections({});
        setFormData({});
    };

    useImperativeHandle(ref, () => ({
        onUpdateFilter: updatedFilters => {
            setFormData(updatedFilters);
        },
    }));

    const renderOptions = (type: string, options: string[], key: string[]): JSX.Element | null => {
        const fieldStyle = {
            bgcolor: theme[themeStyle].gray_text,
            borderRadius: '8px',
            input: { color: theme[themeStyle].tag_white },
            label: { color: 'grey.300' },
            '& .MuiOutlinedInput-notchedOutline': {
                borderColor: 'grey.600',
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: 'grey.400',
            },
            '& .Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: 'grey.300',
            },
        };
        switch (type) {
            case 'checkbox':
                return (
                    <>
                        {options.map((option, index) => {
                            const field = key[0];
                            return (
                                <FormControlLabel
                                    key={index}
                                    control={
                                        <Checkbox
                                            sx={{
                                                color: theme[themeStyle].tag_white,
                                                '&.Mui-checked': {
                                                    color: theme[themeStyle].highlight, // Color for checked state
                                                },
                                            }}
                                            checked={
                                                formData[field] && formData[field].value
                                                    ? (formData[field]?.value.includes(option) as unknown as boolean)
                                                    : false
                                            }
                                            onChange={e => {
                                                let initialValue =
                                                    formData[field] &&
                                                    formData[field].value &&
                                                    (formData[field].value as string[])
                                                        ? (formData[field].value as string[])
                                                        : [];
                                                const checked = e.target.checked;
                                                if (checked) {
                                                    initialValue.push(option);
                                                } else {
                                                    initialValue = initialValue.filter(selected => selected !== option);
                                                }
                                                handleChange(field, initialValue, initialValue);
                                                // handleFilterDisplayValues(field, initialValue);
                                            }}
                                        />
                                    }
                                    label={option}
                                    sx={{
                                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                        color: theme[themeStyle].tag_white,
                                        textTransform: 'capitalize',
                                        '& .MuiTypography-root': {
                                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                            fontSize: '16px',
                                        },
                                    }}
                                />
                            );
                        })}
                    </>
                );

            case 'radio':
                return (
                    <RadioGroup>
                        {options.map((option, index) => {
                            return (
                                <FormControlLabel
                                    key={index}
                                    control={
                                        <Radio
                                            onChange={e => {
                                                handleChange(key[0], option, option);
                                                // handleFilterDisplayValues(key[0], option);
                                            }}
                                            checked={
                                                formData[key[0]] && formData[key[0]].value
                                                    ? formData[key[0]].value === option
                                                    : false
                                            }
                                            sx={{
                                                color: theme[themeStyle].tag_white,
                                                '&.Mui-checked': {
                                                    color: theme[themeStyle].highlight, // Color for checked state
                                                },
                                            }}
                                        />
                                    }
                                    label={option}
                                    sx={{
                                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                        color: theme[themeStyle].tag_white,
                                        textTransform: 'capitalize',
                                        '& .MuiTypography-root': {
                                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                            fontSize: '16px',
                                        },
                                    }}
                                />
                            );
                        })}
                    </RadioGroup>
                );

            case 'dropdown':
                return (
                    <CustomizedAutocomplete
                        options={[...new Set(options)]}
                        maxSelectable={1}
                        initialValue={formData[key[0]] && formData[key[0]].value ? formData[key[0]].value : undefined}
                        onChange={text => {
                            handleChange(key[0], text, text);
                            // handleFilterDisplayValues(text[0], text);
                        }}
                    />
                );

            case 'range':
                return (
                    <Box sx={{ px: 2, py: 1 }}>
                        <Slider
                            value={sliderValue}
                            onChange={(event, newValue) => handleSliderChange(event, newValue as number[], key)}
                            valueLabelDisplay="auto"
                            min={0}
                            max={100}
                            sx={{
                                color: theme[themeStyle].tag_white,
                            }}
                        />
                    </Box>
                );
            case 'location':
                return isLoaded ? (
                    <div key={key[0]} style={{ marginBottom: '20px' }}>
                        <PlacesAutocomplete
                            value={
                                (formData[key[0]] &&
                                    formData[key[0]].value &&
                                    (formData[key[0]].value.address as string)) ||
                                ''
                            }
                            onChange={address => handleChange(key[0], { address })}
                            onSelect={address => handleLocationSelect(key[0], address)}
                            key={key[0]}>
                            {({ getInputProps, suggestions, getSuggestionItemProps, loading }) => (
                                <div style={{ position: 'relative' }}>
                                    <TextField
                                        fullWidth
                                        margin="normal"
                                        {...getInputProps({
                                            placeholder: 'Search Location ...',
                                        })}
                                        sx={fieldStyle}
                                        InputProps={{
                                            endAdornment: formData[key[0]] ? (
                                                <InputAdornment position="end">
                                                    <IconButton onClick={() => clearLocationInput(key[0])} edge="end">
                                                        <ClearIcon sx={{ color: 'grey.400' }} />
                                                    </IconButton>
                                                </InputAdornment>
                                            ) : null,
                                        }}
                                    />
                                    <div
                                        style={{
                                            position: 'absolute',
                                            zIndex: 10,
                                            backgroundColor: '#1e1e1e',
                                            boxShadow: '0px 4px 6px rgba(0, 0, 0, 0.1)',
                                            borderRadius: '8px',
                                            marginTop: '4px',
                                            width: '100%',
                                        }}>
                                        {loading && <div>Loading...</div>}
                                        {suggestions.map(suggestion => {
                                            const style = {
                                                backgroundColor: suggestion.active ? '#2e2e2e' : '#1e1e1e',
                                                cursor: 'pointer',
                                                padding: '10px',
                                                color: '#fff',
                                            };
                                            return (
                                                <div
                                                    {...getSuggestionItemProps(suggestion, {
                                                        style,
                                                    })}
                                                    key={suggestion.placeId}>
                                                    {suggestion.description}
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}
                        </PlacesAutocomplete>
                    </div>
                ) : (
                    <div />
                );
            case 'date-range':
                return (
                    <Box
                        sx={{
                            width: '100%', // Make the container full width
                            '& .rdrCalendarWrapper, & .rdrDateRangeWrapper': {
                                width: '100%',
                            },
                            '& .rdrMonth': {
                                width: '100% !important',
                            },
                            '& .rdrDateDisplayWrapper': {
                                backgroundColor: theme[themeStyle].secondaryBg,
                            },
                            '& .rdrCalendarWrapper': {
                                backgroundColor: theme[themeStyle].secondaryBg, // Background color
                                color: theme[themeStyle].tag_white, // Text color
                            },
                            '& .rdrDayDisabled': {
                                backgroundColor: theme[themeStyle].darkGray60, // Background color
                            },
                            '& .rdrDateRangeWrapper': {
                                backgroundColor: theme[themeStyle].secondaryBg, // Background color
                                color: theme[themeStyle].tag_white, // Text
                            },
                            '& .rdrDayNumber span': {
                                color: theme[themeStyle].tag_white, // Day number text color
                            },
                            '& .rdrDayToday .rdrDayNumber span': {
                                color: '#68d391', // Today’s date text color
                            },
                            '& .rdrSelected, & .rdrStartEdge, & .rdrEndEdge': {
                                backgroundColor: '#63b3ed !important', // Selected range background color
                                color: '#ffffff !important', // Text color inside the range
                            },
                            // '& .rdrDayHovered': {
                            //     backgroundColor: '#90cdf4 !important', // Hovered day background
                            // },
                        }}>
                        <DateRange
                            editableDateInputs={true}
                            onChange={item => {
                                setState([item.selection]);

                                // Convert to required format: YYYY-MM-DDTHH:mm:ss.SSS+00:00
                                const formattedStartDate = moment
                                    .utc(item.selection.startDate)
                                    .format('YYYY-MM-DDTHH:mm:ss.SSS+00:00');
                                const formattedEndDate = moment
                                    .utc(item.selection.endDate)
                                    .format('YYYY-MM-DDTHH:mm:ss.SSS+00:00');

                                const displayDateRange = `${formatDate(
                                    item.selection.startDate,
                                    'MMM Do YYYY',
                                )} - ${formatDate(item.selection.endDate, 'MMM Do YYYY')}`;

                                if (key[0]) {
                                    handleChange(key[0], formattedStartDate, displayDateRange);
                                }
                                if (key[1]) {
                                    handleChange(key[1], formattedEndDate, displayDateRange);
                                }
                            }}
                            moveRangeOnFirstSelection={false}
                            ranges={state}
                            locale={locale}
                            minDate={new Date()}
                        />
                    </Box>
                );
            default:
                return null;
        }
    };

    const clearSpecificFilter = (type: string, key: string[]) => {
        const storeKey = key[0];
        switch (type) {
            case 'checkbox':
            case 'radio':
            case 'dropdown':
            case 'location':
                setFormData(prevData => ({
                    ...prevData,
                    [storeKey]: undefined,
                }));
                break;
            case 'range':
                setSliderValue([0, 100]);
                setFormData(prevData => ({
                    ...prevData,
                    [storeKey]: undefined,
                }));
                break;
            case 'date-range':
                setState([
                    {
                        startDate: new Date(),
                        endDate: new Date(),
                        key: 'selection',
                        color: '#ff6347', // Custom color for the selected range
                    },
                ]);
                setFormData(prevData => ({
                    ...prevData,
                    [storeKey]: undefined,
                }));

                break;
        }
    };

    return (
        <Box
            sx={{
                maxWidth: {
                    xs: '100%',
                    sm: '300px',
                },
                mb: 20,
            }}>
            <Stack my={2} direction="row" justifyContent="space-between" alignItems="center">
                <Typography
                    sx={{
                        fontSize: 16,
                        color: theme[themeStyle].tag_white,
                        fontFamily: FONT_ENUM.MAIN_BOLD,
                    }}>
                    Filters
                </Typography>
                <Typography
                    onClick={handleOnClearFilter}
                    sx={{
                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                        border: `1px solid ${theme[themeStyle].tag_white}`,
                        fontSize: 8,
                        color: theme[themeStyle].tag_white,
                        p: 1,
                        cursor: 'pointer',
                    }}>
                    Clear all Filters
                </Typography>
            </Stack>
            {filter?.filter.map(({ name, options, type, field_name, _id }) => (
                <Accordion
                    key={_id}
                    expanded={!!activeSections[name]}
                    onChange={() => toggleSection(name)}
                    elevation={0}
                    sx={{ mb: 1 }}>
                    <AccordionSummary
                        sx={{ backgroundColor: theme[themeStyle].deep_grey, height: '70px' }}
                        expandIcon={
                            activeSections[name] ? (
                                <Remove
                                    sx={{
                                        color: theme[themeStyle].tag_white,
                                    }}
                                />
                            ) : (
                                <Add
                                    sx={{
                                        color: theme[themeStyle].tag_white,
                                    }}
                                />
                            )
                        }>
                        <Typography
                            sx={{
                                fontSize: 14,
                                color: theme[themeStyle].tag_white,
                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                textTransform: 'uppercase',
                                fontWeight: '600',
                            }}>
                            {name}
                        </Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ backgroundColor: theme[themeStyle].secondaryBg }}>
                        <Stack sx={{ alignItems: 'flex-end' }}>
                            <Typography
                                onClick={() => clearSpecificFilter(type, field_name)}
                                sx={{
                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    border: `1px solid ${theme[themeStyle].tag_white}`,
                                    fontSize: 8,
                                    color: theme[themeStyle].tag_white,
                                    p: 1,
                                    cursor: 'pointer',
                                }}>
                                Clear {name} Filter
                            </Typography>
                        </Stack>
                        {renderOptions(type, options!, field_name)}
                    </AccordionDetails>
                </Accordion>
            ))}
            <CustomButton
                label="Apply"
                sx={{ width: '100%', pt: 4, my: 2 }}
                customStyle={{ borderRadius: '4px' }}
                disabled={isEmpty(formData)}
                onClick={handleOnApplyClick}
            />
        </Box>
    );
});

export { Filter };
